import { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { useReversedMultiReferenceState } from '@/hooks/useReversedMultiReferenceState';
import { areLooselyEqual } from '@/services/utils';
import { PersistedItem } from '@/types/DataModel';

export const useMultiReferenceForm = (
    fieldPath: string,
    isReverseMultiReferenceAttribute: boolean,
    loadingInitialItems: boolean,
) => {
    const { getValues, setValue } = useFormContext();

    // Global state for tracking multi-reference changes
    const { addChange } = useReversedMultiReferenceState();

    // Track previous values to detect updates
    const previousValues = useRef<PersistedItem[]>([]);
    // Flag to prevent setting dirty state during initial load
    const isInitialLoad = useRef(true);

    const { append, fields, remove: originalRemove, replace } = useFieldArray({
        name: fieldPath,
    });

    // Watch for changes to detect updates
    const currentValue = useWatch({
        name: fieldPath
    });

    // Set initial load flag to false after first load
    useEffect(() => {
        if (!loadingInitialItems) {
            isInitialLoad.current = false;
        }
    }, [loadingInitialItems]);

    // Reset the initial load
    const resetInitialLoad = () => {
        isInitialLoad.current = true;
        previousValues.current = [];
    };

    // Helper function to set value with appropriate dirty state
    const setValueWithDirtyState = (path: string, value: any, options?: { shouldDirty?: boolean }) => {
        const shouldDirty = options?.shouldDirty ?? (!isInitialLoad.current && !loadingInitialItems);
        setValue(path, value, { shouldDirty });
    };

    const remove = (index: number) => {
        const currentValue = getValues(fieldPath);
        const itemToRemove = currentValue[index];
        if (itemToRemove) {
            // Track the removal in global state

            if (isReverseMultiReferenceAttribute) {
                addChange(fieldPath, 'remove', itemToRemove, index);
            }

            originalRemove(index);
            // 1. Create a new array without the removed item
            const newValue = currentValue.filter((_: any, i: number) => i !== index);

            // 2. Replace the form state with the new array
            setValueWithDirtyState(fieldPath, newValue, { shouldDirty: true });

            // Reset previous values to avoid false update detection after add
            setTimeout(() => {
                previousValues.current = [];
            }, 0);
        }
    };

    const appendItem = (item: any) => {
        // Remove the properties we added for the Combobox and only append the item
        // props
        delete item.key;
        delete item.label;
        if (isReverseMultiReferenceAttribute) {
        // Track the addition in global state
            addChange(fieldPath, 'add', item);
        }

        append(item);

        // Reset previous values to avoid false update detection after add
        setTimeout(() => {
            previousValues.current = [];
        }, 0);
    };
    // Effect to detect value changes and add action__: 'update' flag
    useEffect(() => {
        // Skip processing during initial loading to prevent false dirty states
        if (loadingInitialItems || !isReverseMultiReferenceAttribute) {
            return;
        }

        if (!currentValue || !Array.isArray(currentValue)) {
            previousValues.current = [];
            return;
        }

        if (previousValues.current.length === 0) {
            previousValues.current = [...currentValue];
            return;
        }

        if (currentValue.length !== previousValues.current.length) {
            previousValues.current = [...currentValue];
            return;
        }

        currentValue?.map((currentItem: PersistedItem, index: number) => {
            const previousItem = previousValues.current[index];

            if (!previousItem) {
                return currentItem;
            }

            if (currentItem.action__) {
                return currentItem;
            }

            const { action__: _, ...curr } = currentItem;
            const { action__: __, ...prev } = previousItem;

            const hasChanged = !areLooselyEqual(curr, prev);

            if (hasChanged) {
                addChange(fieldPath, 'update', currentItem, index);
            }

            return currentItem;
        });
        previousValues.current = [...currentValue];
        // Mark initial load as complete after first successful comparison
        if (isInitialLoad.current && currentValue && currentValue.length > 0 && !loadingInitialItems) {
            isInitialLoad.current = false;
        }
    }, [currentValue, setValue, fieldPath, loadingInitialItems]);

    return {
        fields,
        remove,
        appendItem,
        setValueWithDirtyState,
        replace,
        getValues,
        setValue,
        resetInitialLoad,
    };
};
